import { logger } from '../utils/logger'

const DB_NAME = 'MetricsParquetCache';
const DB_VERSION = 2;
const STORE_NAME = 'parquet_files';

export const openIndexedDB = () => {
  return new Promise((resolve, reject) => {
    logger.debug(`[IDB] Opening IndexedDB "${DB_NAME}" version ${DB_VERSION}`);
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error(`[IDB] openIndexedDB error:`, request.error);
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      logger.debug(`[IDB] openIndexedDB success — actual version: ${db.version}, stores: [${[...db.objectStoreNames]}]`);
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      logger.debug(`[IDB] onupgradeneeded fired — oldVersion: ${event.oldVersion}, newVersion: ${event.newVersion}`);
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        logger.debug(`[IDB] Creating store "${STORE_NAME}" with type index`);
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('type', 'type', { unique: false });
      } else {
        const store = event.target.transaction.objectStore(STORE_NAME);
        logger.debug(`[IDB] Store exists — indexNames: [${[...store.indexNames]}]`);
        if (!store.indexNames.contains('type')) {
          logger.debug(`[IDB] Adding missing type index`);
          store.createIndex('type', 'type', { unique: false });
        }
      }
    };
  });
};

export const cacheParquetFile = async (type, fileName, blob) => {
  const db = await openIndexedDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);

  const key = `${type}_${fileName}`;
  const record = {
    key,
    type,
    fileName,
    blob,
    timestamp: Date.now()
  };

  return new Promise((resolve, reject) => {
    const request = store.put(record);
    request.onsuccess = () => {

      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

export const getCachedParquetFile = async (type, fileName) => {
  const db = await openIndexedDB();
  const transaction = db.transaction([STORE_NAME], 'readonly');
  const store = transaction.objectStore(STORE_NAME);

  const key = `${type}_${fileName}`;

  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => {
      if (request.result) {

        resolve(request.result);
      } else {

        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all cached parquet files for a specific type from IndexedDB
 * @param {string} type - 'web' or 'discover'
 * @returns {Promise<Array>} Array of cached file records with fileName and blob
 */
export const getAllCachedFiles = async (type) => {
  logger.debug(`[IDB] getAllCachedFiles called with type="${type}"`);
  const db = await openIndexedDB();
  logger.debug(`[IDB] db.version=${db.version}, objectStoreNames=[${[...db.objectStoreNames]}]`);
  const transaction = db.transaction([STORE_NAME], 'readonly');
  const store = transaction.objectStore(STORE_NAME);
  logger.debug(`[IDB] store.indexNames=[${[...store.indexNames]}]`);

  return new Promise((resolve, reject) => {
    // Try the type index first; fall back to getAll + filter if index is missing
    let request;
    if (store.indexNames.contains('type')) {
      logger.debug(`[IDB] Using type index for query`);
      request = store.index('type').getAll(IDBKeyRange.only(type));
    } else {
      logger.debug(`[IDB] FALLBACK: type index missing, using getAll + filter`);
      request = store.getAll();
    }
    request.onsuccess = () => {
      const typeFiles = (request.result || [])
        .filter(record => record.type === type)
        .map(record => ({
          fileName: record.fileName,
          blob: record.blob,
          timestamp: record.timestamp
        }));
      resolve(typeFiles);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete specific cached parquet files for a type from IndexedDB.
 * @param {string} type - 'web' or 'discover'
 * @param {string[]} fileNames - bare file names (without the type_ prefix)
 */
export const deleteCachedFiles = async (type, fileNames) => {
  if (!fileNames || fileNames.length === 0) return;
  const db = await openIndexedDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);

  await Promise.all(fileNames.map(fileName => new Promise((resolve, reject) => {
    const request = store.delete(`${type}_${fileName}`);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  })));
};

/**
 * Delete all cached files of a given type from IndexedDB.
 * @param {string} type - 'web' or 'discover'
 */
export const clearCachedFilesByType = async (type) => {
  const db = await openIndexedDB();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  const index = store.index('type');

  await new Promise((resolve, reject) => {
    const request = index.openCursor(IDBKeyRange.only(type));
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
};

export const clearIndexedDBCache = async () => {
  try {
    logger.log('🗑️ Deleting IndexedDB databases...');

    // Close any existing connections first
    try {
      const existingDB = await openIndexedDB();
      existingDB.close();
    } catch (e) {
      // No existing connection, that's fine
    }

    // Delete MetricsParquetCache database
    try {
      await new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => {
          logger.debug('[IDB] MetricsParquetCache deleted');
          resolve(true);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
          logger.warn('[IDB] MetricsParquetCache delete BLOCKED — another tab has an open connection');
          resolve(true);
        };
      });
    } catch (error) {
      logger.warn('[IDB] MetricsParquetCache not found or already deleted');
    }

    // Delete WebMetricsDB database if it exists
    try {
      await new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase('WebMetricsDB');
        request.onsuccess = () => {
          logger.debug('[IDB] WebMetricsDB deleted');
          resolve(true);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
          logger.warn('[IDB] WebMetricsDB delete BLOCKED — another tab has an open connection');
          resolve(true);
        };
      });
    } catch (webMetricsError) {
      logger.warn('[IDB] WebMetricsDB not found or already deleted');
    }

    // logger.log('✅ All IndexedDB databases deleted successfully');
    return true;
  } catch (error) {
    // logger.log('❌ Error deleting IndexedDB databases', error);
    throw error;
  }
};
